/**
 * QUIPWA — domain types
 * Derived from QUIPWA_SPEC v1.0 — do not modify structure without explicit versioning.
 */

// ─── NOTE TYPES ──────────────────────────────────────────────────────────────

export const NOTE_TYPE = {
  BASIC: 0,
  BASIC_REV: 1,  // generates 2 cards
  CLOZE: 2,      // fill-in-the-blank
  ELABORATION: 3 // long-form, Pro
} as const;
export type NoteType = typeof NOTE_TYPE[keyof typeof NOTE_TYPE];

// ─── CARD STATES (FSRS) ──────────────────────────────────────────────────────

export const CARD_STATE = {
  NEW: 0,
  LEARNING: 1,
  REVIEW: 2,
  RELEARNING: 3
} as const;
export type CardState = typeof CARD_STATE[keyof typeof CARD_STATE];

// ─── SESSION MODES ───────────────────────────────────────────────────────────

export const SESSION_MODE = {
  FLIP: 0,
  MIXED: 1,
  FREE: 2
} as const;
export type SessionMode = typeof SESSION_MODE[keyof typeof SESSION_MODE];

// ─── .wflash CONTENT TYPES ───────────────────────────────────────────────────

export interface WFlashManifest {
  wflash_version: '1.0';
  schema_version: 1;
  deck_id: string;        // UUID v4
  deck_name: string;
  description: string | null;
  created_at: number;     // unix timestamp
  updated_at: number;
  card_count: number;
  note_count: number;
  media_count: number;
}

export interface Deck {
  id: string;
  name: string;
  description: string | null;
  created_at: number;
  updated_at: number;
}

export interface NoteTypeRow {
  id: NoteType;
  name: 'basic' | 'basic_rev' | 'cloze' | 'elaboration';
  fields: string; // JSON array: ["Front","Back"]
}

export interface Note {
  id: string;
  deck_id: string;
  note_type: NoteType;
  created_at: number;
  updated_at: number;
}

export interface NoteField {
  note_id: string;
  field_ord: 0 | 1;   // 0=Front/Text/Prompt, 1=Back/Extra/Guide
  value: string;       // plain text, HTML, or LaTeX with $...$
}

export interface Card {
  id: string;
  note_id: string;
  deck_id: string;
  template_ord: 0 | 1;  // 0=normal, 1=reverse
  card_type: NoteType;
  created_at: number;
}

export interface MediaRecord {
  hash: string;     // SHA-256 hex
  filename: string;
  mime_type: string;
  size: number;
}

export interface CardMedia {
  card_id: string;
  hash: string;
  face: 'front' | 'back';
}

// ─── _state.db TYPES ─────────────────────────────────────────────────────────

export interface DeckInstance {
  id: string;              // UUID v4 generated at import time
  deck_id: string;         // deck_id from manifest
  deck_name: string;
  wflash_drive_path: string;
  drive_file_id: string;
  drive_modified_at: number;
  imported_at: number;
  card_count: number;
  card_style?: string | null;
  deck_color?: string | null;
}

export interface Tag {
  id: string;              // UUID v4
  name: string;            // unique, lowercase usually
  parent_id: string | null; // hierarchy: tag -> subtag
  color: string;           // #FFF, var(--c-blue)
  created_at: number;
}

export interface TagWithStats extends Tag {
  decks_count: number;
  cards_count: number;
}

export interface TagsJSON {
  version: 1;
  tags: Tag[];
  deck_tags: Record<string, string[]>; // deck_instance_id -> list of tag_id
}

export interface ReviewState {
  card_id: string;
  deck_instance_id: string;
  due: number;             // unix timestamp
  stability: number;       // S: days to 90% retention
  difficulty: number;      // D: 1-10
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: CardState;
  last_review: number | null;
  avg_time_ms: number | null;
}

export interface ReviewLog {
  id?: number;             // AUTOINCREMENT
  card_id: string;
  deck_instance_id: string;
  session_id: string;
  reviewed_at: number;
  rating: 1 | 2 | 3 | 4 | 5;
  time_ms: number;
  stability_before: number;
  stability_after: number;
  difficulty_before: number;
  difficulty_after: number;
  scheduled_days_before: number;
  scheduled_days_after: number;
}

export interface DeckStats {
  deck_instance_id: string;
  total_cards: number;
  new_cards: number;
  learning_cards: number;
  young_cards: number;      // state=2, scheduled_days < 21
  mature_cards: number;     // state=2, scheduled_days >= 21
  due_today: number;
  overdue_cards: number;
  future_cards?: number;
  avg_stability: number | null;
  avg_retention: number | null;
  avg_time_ms: number | null;
  streak_days: number;
  last_studied_at: number | null;
  first_studied_at?: number | null;
  updated_at: number;
}

export interface DeckWithStats {
  instance: DeckInstance;
  stats: DeckStats | null;
}

export interface Session {
  id: string;
  started_at: number;
  ended_at: number | null;
  mode: SessionMode;
  cards_total: number;
  cards_reviewed: number;
  avg_time_ms: number | null;
  cal_event_id: string | null;
}

export interface SessionDeck {
  session_id: string;
  deck_instance_id: string;
  cards_from_deck: number;
}

// ─── USER SETTINGS ───────────────────────────────────────────────────────────

export interface UserSettings {
  new_cards_per_day: number;           // 10-30, default 20
  max_reviews_per_day: number;         // default 200
  target_session_minutes: number;      // default 30
  default_session_mode: SessionMode;
  /** FSRS target probability of recall at review time (0.80–0.97). Higher means more reviews. */
  desired_retention: number;           // default 0.90
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  new_cards_per_day: 20,
  max_reviews_per_day: 200,
  target_session_minutes: 30,
  default_session_mode: SESSION_MODE.FLIP,
  desired_retention: 0.9,
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Card with its fields hydrated (used in session/editor views) */
export interface HydratedCard {
  card: Card;
  note: Note;
  front: string;
  back: string;
  review_state: ReviewState | null;
}

// ─── NOTES MODULE (_state.db) ────────────────────────────────────────────────
// NOTE: named `NoteRecord` (not `Note`) to avoid colliding with the Anki-style
// `Note` interface above, which belongs to the flashcard/collection.db model.

export interface NoteRecord {
  id: string;                    // local UUID v4
  drive_file_id: string | null;
  parent_id: string | null;      // hierarchy: Long Course -> child notes
  drive_folder_id: string | null; // set when type promotes to a real Drive subfolder
  title: string;
  type: string;                  // 'Book' | 'Quick Note' | 'Short Course' | 'Long Course'
  status: string;                // 'Pending' | 'In Progress' | 'Completed' | 'Mastered'
  domain: string | null;
  content_cache: string | null;  // full Markdown, cached for offline reads
  content_synced_at: number | null;
  drive_modified_at: number | null;
  local_modified_at: number;
  checksum: string | null;
  deleted: 0 | 1;   // SQLite INTEGER, mirrors how sql.js returns the column — not a JS boolean
}

export interface NoteLink {
  id?: number;                   // AUTOINCREMENT
  from_note_id: string;
  to_note_id: string;
  link_text: string;
}

// ─── SYNC & CONFLICT TYPES (Phase 1A) ──────────────────────────────────────

export interface SyncConflict {
  conflictId: string;
  type: 'review_state' | 'deck_stats';
  key: string;
  base: ReviewState | DeckStats | null;
  local: ReviewState | DeckStats | null;
  remote: ReviewState | DeckStats | null;
  timestamp: number;
}

export type SyncConflictResolution = 'keep_local' | 'keep_remote' | 'manual_merge';

export interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  hasPendingOperations: boolean;
  hasConflicts: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
}
